import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { reportError } from '../../lib/sentry';
import { getApiUrl } from '../../lib/services/urls';
import { DS } from '../PatientForms/designTokens';

interface PatientAuthContextType {
  isAuthenticated: boolean;
  token: string | null;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(
  undefined
);

export const PATIENT_TOKEN_KEY = 'patientFormToken';

export function PatientAuthProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const code = searchParams.get('code');
    const existingToken = sessionStorage.getItem(PATIENT_TOKEN_KEY);

    const exchangeCode = async (oneTimeCode: string) => {
      try {
        const res = await axios.post(`${getApiUrl()}forms/exchange-code`, {
          code: oneTimeCode,
        });
        const t = res.data.result.token;
        sessionStorage.setItem(PATIENT_TOKEN_KEY, t);
        return t;
      } catch (err) {
        reportError(err);
        sessionStorage.removeItem(PATIENT_TOKEN_KEY);
        return null;
      }
    };

    const init = async () => {
      let t = existingToken;

      if (code) {
        t = await exchangeCode(code);
        const params = new URLSearchParams(searchParams);
        params.delete('code');
        setSearchParams(params, { replace: true });
      }

      if (!t) {
        setLoading(false);
        return;
      }

      setToken(t);
      setIsAuthenticated(true);
      setLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the one-time code is exchanged once on mount; re-running when the URL changes would re-exchange it
  }, []);

  const contextValue = useMemo(
    () => ({ isAuthenticated, token }),
    [isAuthenticated, token]
  );

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#F0EDE6',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            p: 2,
            width: '100%',
            maxWidth: '384px',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              px: 2.5,
              py: 3.5,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                fontSize: '17px',
                fontWeight: 600,
                color: DS.deepTeal,
                mb: 1,
                fontFamily: DS.fontDisplay,
              }}
            >
              Sessionen har gått ut
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#6B7280',
                lineHeight: 1.5,
                fontFamily: DS.fontBody,
              }}
            >
              Stäng sidan och öppna formuläret från appen igen.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <PatientAuthContext.Provider value={contextValue}>
      {children}
    </PatientAuthContext.Provider>
  );
}
